import type { UppFloorPlanStageComponent } from '../canvas/upp-floor-plan-stage.component';
import { konvaShapeByUuidFromModel } from '../internal/upp-konva-model-resolve-shape';
import type { UppKonvaFloorPlanModel } from '../internal/upp-konva-floor-plan-model';
import { UppFloorPlanDrawGroup } from './upp-floor-plan-draw-group';
import type { UppKonvaElementKind } from '../internal/upp-konva-scene-ref';
import type { UppKonvaShapeHandle } from './upp-konva-shape-handle';
import type { UppKonvaAxis } from '../../model/upp-konva-types';

/** Axis used by {@link UppKonvaFloorPlanGroup.align} and {@link UppKonvaFloorPlanGroup.distribute}. */
export type UppKonvaFloorPlanGroupAxis = UppKonvaAxis;

/**
 * Multi-selection on stage: list of {@link UppKonvaShapeHandle} plus align/distribute/geometry operations.
 */
export class UppKonvaFloorPlanGroup {
  private readonly _delegate: UppFloorPlanDrawGroup;

  constructor(
    private readonly _floor: UppFloorPlanStageComponent,
    private readonly _model: UppKonvaFloorPlanModel,
  ) {
    this._delegate = new UppFloorPlanDrawGroup(_floor);
  }

  /** Handles of selected rows (same order as stage selection). */
  get selection(): readonly UppKonvaShapeHandle[] {
    return this._selectionShapes();
  }

  /**
   * Replaces stage selection with UUIDs from provided shapes (delegates to internal stage **`selectOnly`**).
   */
  set selection(next: readonly UppKonvaShapeHandle[]) {
    this._floor.selectOnly(next.map((s) => s.uuid));
  }

  private _selectionShapes(): UppKonvaShapeHandle[] {
    const out: UppKonvaShapeHandle[] = [];
    for (const el of this._floor.getSelection()) {
      const s = konvaShapeByUuidFromModel(this._model, el.uuid);
      if (s) {
        out.push(s);
      }
    }
    return out;
  }

  /**
   * Common logical type ({@link UppKonvaShapeHandle.type}) when all selected items match and there are **>= 2** items;
   * returns **`null`** when types are mixed or stage selection has **&lt; 2** items.
   */
  get type(): UppKonvaElementKind | null {
    const sh = this._selectionShapes();
    if (sh.length < 2) {
      return null;
    }
    const t0 = sh[0]?.type;
    if (!t0) {
      return null;
    }
    return sh.every((x) => x.type === t0) ? t0 : null;
  }

  private _uniformScalar(read: (s: UppKonvaShapeHandle) => number): number | null {
    if (this.type === null) {
      return null;
    }
    const sh = this._selectionShapes();
    if (sh.length === 0) {
      return null;
    }
    const first = sh[0];
    if (!first) {
      return null;
    }
    const v0 = read(first);
    return sh.every((s) => read(s) === v0) ? v0 : null;
  }

  get x(): number | null {
    return this._uniformScalar((s) => s.x);
  }

  set x(v: number) {
    if (!this._guardGeom('x') || !Number.isFinite(v)) {
      return;
    }
    const sh = this._selectionShapes();
    const first = sh[0];
    if (!first) {
      return;
    }
    const dx = v - first.x;
    if (dx === 0) {
      return;
    }
    for (const s of sh) {
      s.x = s.x + dx;
    }
  }

  get y(): number | null {
    return this._uniformScalar((s) => s.y);
  }

  set y(v: number) {
    if (!this._guardGeom('y') || !Number.isFinite(v)) {
      return;
    }
    const sh = this._selectionShapes();
    const first = sh[0];
    if (!first) {
      return;
    }
    const dy = v - first.y;
    if (dy === 0) {
      return;
    }
    for (const s of sh) {
      s.y = s.y + dy;
    }
  }

  get width(): number | null {
    return this._uniformScalar((s) => s.width);
  }

  set width(v: number) {
    if (!this._guardGeom('width') || !Number.isFinite(v)) {
      return;
    }
    const sh = this._selectionShapes();
    const first = sh[0];
    if (!first) {
      return;
    }
    this._delegate.applyUniformGeometry(v, first.height, first.rotation);
  }

  get height(): number | null {
    return this._uniformScalar((s) => s.height);
  }

  set height(v: number) {
    if (!this._guardGeom('height') || !Number.isFinite(v)) {
      return;
    }
    const sh = this._selectionShapes();
    const first = sh[0];
    if (!first) {
      return;
    }
    this._delegate.applyUniformGeometry(first.width, v, first.rotation);
  }

  get rotation(): number | null {
    return this._uniformScalar((s) => s.rotation);
  }

  set rotation(v: number) {
    if (!this._guardGeom('rotation') || !Number.isFinite(v)) {
      return;
    }
    const sh = this._selectionShapes();
    const first = sh[0];
    if (!first) {
      return;
    }
    this._delegate.applyUniformGeometry(first.width, first.height, v);
  }

  /** Aligns centers on the specified axis (stage screen coordinates). */
  align(axis: UppKonvaAxis): void {
    const internal = axis === 'VERTICAL' ? 'vertical' : 'horizontal';
    this._delegate.alignCenters(internal);
  }

  /** Distributes uniform spacing on the specified axis (screen projection). */
  distribute(axis: UppKonvaAxis): void {
    const internal = axis === 'VERTICAL' ? 'vertical' : 'horizontal';
    this._delegate.distributeEven(internal);
  }

  /**
   * Applies same geometry (cm and degrees) to each transformable selected item of the same `kind`.
   * @returns `true` when stage applies the change.
   */
  applyUniformGeometry(widthCm: number, heightCm: number, rotationDeg: number): boolean {
    return this._delegate.applyUniformGeometry(widthCm, heightCm, rotationDeg);
  }

  /** Same stage criterion: >=2 transformable items of the same `kind` (e.g. no mixed wall vertices). */
  canGroupResizeSelection(): boolean {
    return this._delegate.canGroupResize();
  }

  /** Misma regla que el stage para alinear/distribuir. */
  selectionIsUniformKind(): boolean {
    return this._delegate.selectionIsUniformKind();
  }

  private _guardGeom(prop: string): boolean {
    if (this.type === null) {
      console.error(
        `upp-konva: UppKonvaGroup: cannot set ${prop} when type is null (fewer than two items, or mixed UppKonvaShapeHandle.type).`,
      );
      return false;
    }
    return true;
  }
}
